import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, ShieldCheck, Handshake, FileCheck2, Users, Compass, ChevronDown, UserCheck, FolderCheck, Sparkles, EyeOff } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";
import { MarketingCapture } from "../components/MarketingCapture";
import { CityStrip } from "../components/CityStrip";
import { SocialProofBar } from "../components/SocialProofBar";

const CITIES = [
  { url: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=1920&q=80", caption: "Sydney, Australia" },
  { url: "https://images.unsplash.com/photo-1718750689942-9f51d49cfcea?auto=format&fit=crop&w=1920&q=80", caption: "The Australian coast" },
  { url: "https://images.unsplash.com/photo-1514395462725-fb4566210144?auto=format&fit=crop&w=1920&q=80", caption: "Melbourne, Australia" },
  { url: "https://images.unsplash.com/photo-1610263659457-27ef5a68a423?auto=format&fit=crop&w=1920&q=80", caption: "Coastal city living" },
];

const VIDEOS = [
  { url: "https://videos.pexels.com/video-files/1739010/1739010-hd_1920_1080_30fps.mp4", caption: "The Australian coastline" },
  { url: "https://videos.pexels.com/video-files/3121459/3121459-uhd_2560_1440_24fps.mp4", caption: "City life, Australia" },
];

const clamp = (n) => Math.min(1, Math.max(0, n));

export default function Home() {
  const [idx, setIdx] = useState(0);
  const [vidIdx, setVidIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const heroRef = useRef(null);
  const videoRefs = useRef([]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % CITIES.length), 10000);
    const v = setInterval(() => setVidIdx((i) => (i + 1) % VIDEOS.length), 14000);
    return () => { clearInterval(t); clearInterval(v); };
  }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = heroRef.current;
        if (!el) return;
        const total = el.offsetHeight - window.innerHeight;
        const y = Math.min(Math.max(-el.getBoundingClientRect().top, 0), total);
        setProgress(total > 0 ? y / total : 0);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const toggleVideos = () => {
    setPaused((p) => {
      videoRefs.current.forEach((v) => { if (v) { p ? v.play() : v.pause(); } });
      return !p;
    });
  };

  const photoOpacity = videoReady ? clamp(1 - progress * 1.8) : 1;
  const videoOpacity = videoReady ? clamp(progress * 1.6) : 0;
  const logoOpacity = clamp(1 - progress * 2.2);
  const introOpacity = clamp((progress - 0.4) * 2.4);

  return (
    <div className="min-h-screen bg-[#fcfbf8]">
      <PublicHeader />
      <section ref={heroRef} className="relative" style={{ height: "230vh" }} data-testid="hero-section">
        <div className="sticky top-0 h-screen min-h-[640px] overflow-hidden">
          <div className="absolute inset-0" style={{ opacity: photoOpacity }} data-testid="hero-photo-layer">
            {CITIES.map((c, i) => (
              <div key={c.url} className={`absolute inset-0 transition-opacity duration-[3500ms] ease-in-out ${i === idx ? "opacity-100" : "opacity-0"}`}>
                <img src={c.url} alt={c.caption} className="w-full h-full object-cover kenburns" />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 cursor-pointer" onClick={toggleVideos} data-testid="hero-video-layer"
            style={{ opacity: videoOpacity, filter: `grayscale(${clamp(1 - progress * 1.1)}) saturate(${0.4 + clamp(progress) * 0.8})` }}>
            {VIDEOS.map((v, i) => (
              <video key={v.url} ref={(el) => (videoRefs.current[i] = el)} src={v.url}
                muted loop playsInline autoPlay preload="auto" onCanPlay={() => setVideoReady(true)}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[3000ms] ease-in-out ${i === vidIdx ? "opacity-100" : "opacity-0"}`} />
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d2f4d]/95 via-[#0d2f4d]/45 to-[#0d2f4d]/25 pointer-events-none" />
          <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-32 pt-16 pointer-events-none"
            style={{ opacity: introOpacity, transform: `translateY(${Math.round((1 - introOpacity) * 50)}px)` }} data-testid="hero-intro">
            <p className="font-heading text-xs sm:text-sm font-bold uppercase tracking-[0.35em] text-[#C1CC24]">We are recruiters</p>
            <h1 className="mt-4 text-4xl sm:text-6xl lg:text-7xl font-black uppercase text-white tracking-tighter leading-[0.95] max-w-4xl">
              Australian recruiters.<br />
              <span className="font-accent font-medium normal-case italic text-[#C1CC24]">Every major city.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/85 max-w-xl leading-relaxed">
              Sydney to Perth, Brisbane to Melbourne — we're not a job board, and we're not a
              marketplace. You tell us what you need; we find, vet and place the right people.
              No listings. No CV piles. Just recruiters.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4" style={{ pointerEvents: introOpacity > 0.3 ? "auto" : "none" }}>
              <Link to="/candidates" data-testid="hero-cta-candidate"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-semibold px-8 py-4 hover:bg-[#aeb81f] transition-colors">
                I'm a Candidate <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/employers" data-testid="hero-cta-employer"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-white/10 border border-white/40 text-white font-medium px-8 py-4 backdrop-blur-sm hover:bg-white hover:text-[#0d2f4d] transition-colors">
                I'm an Employer <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
            style={{ opacity: logoOpacity, transform: `scale(${1 + progress * 0.18})` }} data-testid="hero-logo-intro">
            <img src="/logo.png" alt="The Ditch Careers" className="h-44 w-44 sm:h-60 sm:w-60 rounded-full shadow-2xl" />
            <p className="mt-7 font-heading text-sm sm:text-base font-bold uppercase tracking-[0.35em] text-[#C1CC24]">Australian recruiters · Every major city</p>
          </div>
          <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/70 pointer-events-none"
            style={{ opacity: clamp(1 - progress * 3) }} data-testid="scroll-indicator">
            <span className="text-[10px] uppercase tracking-[0.3em]">Scroll to get in</span>
            <ChevronDown size={18} className="mt-2 animate-bounce" />
          </div>
          <p className="absolute bottom-7 right-8 hidden sm:block font-accent text-white/70 text-base pointer-events-none">
            {progress > 0.5 ? `${VIDEOS[vidIdx].caption}${paused ? " · paused" : " · click to pause"}` : CITIES[idx].caption}
          </p>
        </div>
      </section>

      <div className="bg-[#0d2f4d] border-y border-white/10 overflow-hidden py-4" data-testid="marquee-strip">
        <div className="marquee-track flex whitespace-nowrap w-max">
          {[0, 1].map((n) => (
            <span key={n} className="font-heading text-sm tracking-[0.25em] uppercase text-[#C1CC24]">
              {"We find · We vet · We place · Sydney · Melbourne · Brisbane · Perth · Adelaide · Gold Coast · Canberra · Hobart · Darwin · Recruiters, not job boards · Chosen, not browsed · "}
            </span>
          ))}
        </div>
      </div>

      <CityStrip />
      <SocialProofBar />

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="ditch-card p-10" data-testid="home-candidates-card">
            <Compass className="text-[#7C8514]" size={28} />
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] mt-5 tracking-tight">How we help candidates</h2>
            <ul className="mt-6 space-y-4 text-[#51637a] text-base leading-relaxed">
              <li className="flex gap-3"><Globe2 size={18} className="text-[#0d2f4d] mt-1 shrink-0" /> Jobs across every major Australian city — whether you're already here or moving from overseas, we sort visas, work rights and the move.</li>
              <li className="flex gap-3"><FileCheck2 size={18} className="text-[#0d2f4d] mt-1 shrink-0" /> Upload your CV once — we turn it into a complete, recruiter-ready profile. You stay in control.</li>
              <li className="flex gap-3"><Handshake size={18} className="text-[#0d2f4d] mt-1 shrink-0" /> Real recruiters match you to roles — you're never just a number in a job board.</li>
            </ul>
            <Link to="/candidates" data-testid="home-candidates-link" className="inline-flex items-center gap-2 mt-8 text-[#7C8514] font-medium hover:gap-3 transition-all">
              Explore the candidate journey <ArrowRight size={16} />
            </Link>
          </div>
          <div className="ditch-card p-10" data-testid="home-employers-card">
            <Users className="text-[#7C8514]" size={28} />
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] mt-5 tracking-tight">How we help employers</h2>
            <ul className="mt-6 space-y-4 text-[#51637a] text-base leading-relaxed">
              <li className="flex gap-3"><ShieldCheck size={18} className="text-[#0d2f4d] mt-1 shrink-0" /> You don't post a job — you brief us. We advertise on LinkedIn and Seek, search our pool and headhunt on your behalf.</li>
              <li className="flex gap-3"><FileCheck2 size={18} className="text-[#0d2f4d] mt-1 shrink-0" /> Every candidate is vetted by us first — credentials, references, work rights, the lot.</li>
              <li className="flex gap-3"><Handshake size={18} className="text-[#0d2f4d] mt-1 shrink-0" /> One-off placements, or subscription partnerships for agencies with ongoing hiring needs.</li>
            </ul>
            <Link to="/employers" data-testid="home-employers-link" className="inline-flex items-center gap-2 mt-8 text-[#7C8514] font-medium hover:gap-3 transition-all">
              See how hiring works <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#0d2f4d] relative grain" data-testid="cv-creator-strip">
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <p className="font-accent text-lg text-[#C1CC24]">The CV creator</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3 tracking-tight max-w-2xl">
            Upload your CV once. We turn it into a recruiter-ready profile.
          </h2>
          <p className="mt-5 text-base text-white/70 max-w-xl leading-relaxed">
            No forms to fill out from scratch. Our smart CV reader pulls your work history, skills and
            qualifications straight from your CV and builds your complete profile in seconds — you just
            review, polish and hit submit.
          </p>
          <div className="mt-12 grid sm:grid-cols-3 gap-10">
            {[
              { icon: Sparkles, title: "Reads your CV for you", text: "Drop in a PDF or Word doc — roles, dates, skills and qualifications are extracted and structured automatically. No retyping your career history into yet another form." },
              { icon: EyeOff, title: "Private by design", text: "Your profile is never public and never searchable. Add companies to your blocked list — like your current employer — and they'll never know you're looking." },
              { icon: Globe2, title: "Work rights, sorted", text: "Citizen, permanent resident or arriving on a visa — we work out your right-to-work pathway from day one, wherever you're coming from." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                  <Icon size={22} className="text-[#C1CC24]" />
                </span>
                <h3 className="font-heading font-bold text-white text-lg mt-5">{title}</h3>
                <p className="text-sm text-white/60 mt-3 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <Link to="/candidates" data-testid="cv-creator-strip-cta" className="inline-flex items-center gap-2 mt-10 text-[#C1CC24] font-medium hover:gap-3 transition-all">
            See how the CV creator works <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="bg-white border-y border-[#e3e0d3]" data-testid="what-makes-us-different">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="font-accent text-lg text-[#7C8514]">What makes us different</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0d2f4d] mt-3 tracking-tight max-w-2xl">
            No listings. No noise. Just placements.
          </h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { icon: UserCheck, title: "Humans, not job boards", text: "Every placement is run by a recruiter who knows the Australian market. You deal with a person, not a portal — no listings, no applying into a void." },
              { icon: FolderCheck, title: "Selective by design", text: "We choose who we work with — candidates and employers. If we take your brief, it's because we know we can fill it. If we take you on, it's because we can place you." },
              { icon: Globe2, title: "We go find them", text: "We run our own campaigns on LinkedIn and Seek, search our talent pool and headhunt directly — then check AU work rights before a shortlist ever lands." },
              { icon: ShieldCheck, title: "Curated & confidential", text: "Employers never trawl a database of profiles. They only ever see the candidates our recruiters hand-pick for their specific role — better matches, faster hires, no noise." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#f5f3ee]">
                  <Icon size={22} className="text-[#0d2f4d]" />
                </span>
                <h3 className="font-heading font-bold text-[#0d2f4d] text-lg mt-5">{title}</h3>
                <p className="text-sm text-[#51637a] mt-3 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="how-it-works">
        <p className="font-accent text-lg text-[#7C8514]">How it works</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0d2f4d] mt-3 tracking-tight max-w-2xl">
          What happens when you sign up — and how long it takes.
        </h2>
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div className="ditch-card p-8" data-testid="how-it-works-candidates">
            <h3 className="font-heading font-bold text-[#0d2f4d] text-xl">For candidates</h3>
            <ol className="mt-6 space-y-6">
              {[
                ["Upload your CV", "2 min", "Drop it in — we auto-build your profile from it."],
                ["Tell us what you want", "3 min", "Role, location, and visa/work rights if you're relocating."],
                ["We match you", "ongoing", "A real recruiter reviews your profile and reaches out when there's a fit."],
              ].map(([t, time, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="shrink-0 h-9 w-9 rounded-full bg-[#0d2f4d] text-white font-heading font-bold text-sm flex items-center justify-center">{i + 1}</span>
                  <div>
                    <p className="font-medium text-[#0d2f4d]">{t} <span className="ml-2 rounded-full bg-[#C1CC24]/20 text-[#7C8514] text-xs font-semibold px-2.5 py-0.5">{time}</span></p>
                    <p className="text-sm text-[#51637a] mt-1 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/candidates" data-testid="how-it-works-candidate-cta" className="inline-flex items-center gap-2 mt-8 text-[#7C8514] font-medium hover:gap-3 transition-all">
              I'm a Candidate <ArrowRight size={16} />
            </Link>
          </div>
          <div className="ditch-card p-8" data-testid="how-it-works-employers">
            <h3 className="font-heading font-bold text-[#0d2f4d] text-xl">For employers</h3>
            <ol className="mt-6 space-y-6">
              {[
                ["Brief us", "5 min", "The role, the seniority, and what \"right\" looks like for your team."],
                ["We go to market", "24–48 hrs", "We search, headhunt and shortlist — you never sift a CV pile."],
                ["Review & place", "your pace", "You only see candidates we've already vetted. Interview, choose, hire."],
              ].map(([t, time, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="shrink-0 h-9 w-9 rounded-full bg-[#0d2f4d] text-white font-heading font-bold text-sm flex items-center justify-center">{i + 1}</span>
                  <div>
                    <p className="font-medium text-[#0d2f4d]">{t} <span className="ml-2 rounded-full bg-[#C1CC24]/20 text-[#7C8514] text-xs font-semibold px-2.5 py-0.5">{time}</span></p>
                    <p className="text-sm text-[#51637a] mt-1 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/employers" data-testid="how-it-works-employer-cta" className="inline-flex items-center gap-2 mt-8 text-[#7C8514] font-medium hover:gap-3 transition-all">
              I'm an Employer <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-28 relative overflow-hidden" data-testid="statement-section">
        <span aria-hidden="true" className="absolute -top-2 left-0 font-heading font-black uppercase text-[#0d2f4d]/[0.04] text-[22vw] leading-none tracking-tighter select-none pointer-events-none whitespace-nowrap">
          AUSTRALIA
        </span>
        <p className="relative font-heading text-4xl sm:text-6xl lg:text-6xl font-extrabold text-[#0d2f4d] tracking-tight leading-[1.05]">
          We <span className="font-accent font-medium text-[#7C8514]">find.</span><br />
          We <span className="font-accent font-medium text-[#7C8514]">vet.</span><br />
          We <span className="font-accent font-medium text-[#7C8514]">place.</span>
        </p>
        <p className="relative mt-8 text-base md:text-lg text-[#51637a] max-w-xl leading-relaxed">
          No listings to scroll. No inbox full of unvetted CVs. Just Australian recruiters who take
          a brief, go to market, and deliver people who can actually do the job.
        </p>
      </section>

      <section className="bg-[#0d2f4d] relative grain">
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
          {[["1", "Brief. That's all we need"], ["100%", "Recruiter-run placements"], ["0", "Job listings to scroll"], ["AUS", "Where we place you"]].map(([n, l]) => (
            <div key={l}>
              <p className="font-heading text-4xl font-extrabold text-white">{n}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-white/50 mt-2">{l}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 text-left">
        <p className="text-xs uppercase tracking-[0.3em] text-[#7C8514] font-medium">Ready when you are</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0d2f4d] mt-4 tracking-tight max-w-2xl">
          Wherever you are in Australia, start here.
        </h2>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link to="/signup/candidate" data-testid="final-cta-candidate"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0d2f4d] text-white font-medium px-8 py-4 hover:bg-[#1c4d78] transition-colors">
            I'm a Candidate
          </Link>
          <Link to="/signup/employer" data-testid="final-cta-employer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium px-8 py-4 hover:bg-[#0d2f4d] hover:text-white transition-colors">
            I'm an Employer
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="soding-bros-band">
        <a href="https://sodingbros.com/our-services/" target="_blank" rel="noreferrer" data-testid="soding-bros-link"
          className="group block border-4 border-[#0d2f4d] bg-[#C1CC24] p-8 sm:p-12 transition-colors hover:bg-[#0d2f4d]">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#0d2f4d] group-hover:text-[#C1CC24]">Real Estate Specialists</p>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-[#0d2f4d] group-hover:text-white leading-tight">
            All real estate candidates &amp; employers are handled by Laura &amp; Priya at Soding Brothers Solutions.
          </h2>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#0d2f4d] group-hover:text-[#C1CC24]">
            View their services <ArrowRight size={18} />
          </span>
        </a>
      </section>

      <MarketingCapture source="home" />
      <SiteFooter />
    </div>
  );
}
